// src/pages/Workshops/components/AddReservationPopup.tsx
import { useState } from 'react';
import { X } from 'lucide-react';
import { addDoc, collection, doc, increment, Timestamp, updateDoc } from 'firebase/firestore';
import { db } from '../../../firebase';

type ReservationType = 'solo' | 'duo';

interface Props {
  workshopId: string;
  onClose: () => void;
  onCreated?: () => void;
}

const AddReservationPopup = ({ workshopId, onClose, onCreated }: Props) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [reservationType, setReservationType] = useState<ReservationType>('solo');
  const [loading, setLoading] = useState(false);
  const [errMsg, setErrMsg] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrMsg(null);

    if (!name.trim()) {
      setErrMsg('Le nom est requis.');
      return;
    }
    if (!email.trim()) {
      setErrMsg("L'email est requis.");
      return;
    }
    const emailOk = /\S+@\S+\.\S+/.test(email);
    if (!emailOk) {
      setErrMsg("L'email n'est pas valide.");
      return;
    }

    setLoading(true);
    try {
      // Une place = un plan de travail. Un duo, c'est deux personnes autour
      // d'un seul plan de travail : il occupe donc une place, comme un solo.
      const placesCount = 1;

      // ✅ 1. Ajout de la réservation
      await addDoc(collection(db, 'reservations'), {
        createdAt: Timestamp.now(),
        workshopId,
        name: name.trim(),
        email: email.trim().toLowerCase(),
        phone: phone.trim() || null,
        type: reservationType,
        places: placesCount,
      });

      // ✅ 2. Décrémente remainingPlaces (1 place, quel que soit le type)
      const workshopRef = doc(db, 'workshops_v2', workshopId);
      await updateDoc(workshopRef, {
        remainingPlaces: increment(-placesCount),
      });

      onCreated?.();
      onClose();
    } catch (err) {
      console.error('❌ Erreur création réservation :', err);
      setErrMsg(
        "Un problème est survenu lors de l'ajout de la réservation. Réessayez."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-lg border border-[#623413]/10">
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h3 className="text-lg font-semibold text-[#623413]">
            Ajouter un participant
          </h3>
          <button
            onClick={onClose}
            className="p-1 rounded hover:bg-gray-100 transition"
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom & Prénom *
            </label>
            <input
              type="text"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#623413]/40"
              placeholder="Ex : Marie Dupont"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email *
            </label>
            <input
              type="email"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#623413]/40"
              placeholder="exemple@mail.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Téléphone
            </label>
            <input
              type="tel"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#623413]/40"
              placeholder="06 12 34 56 78"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de réservation *
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="reservationType"
                  value="solo"
                  checked={reservationType === 'solo'}
                  onChange={() => setReservationType('solo')}
                  className="w-4 h-4 text-[#623413] focus:ring-[#623413]/40"
                />
                <span className="text-sm text-gray-700">Solo (1 place)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="reservationType"
                  value="duo"
                  checked={reservationType === 'duo'}
                  onChange={() => setReservationType('duo')}
                  className="w-4 h-4 text-[#623413] focus:ring-[#623413]/40"
                />
                <span className="text-sm text-gray-700">
                  Duo (1 place, 2 personnes)
                </span>
              </label>
            </div>
          </div>

          {errMsg && <p className="text-sm text-[#BB0F33]">{errMsg}</p>}

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 transition"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-lg bg-[#623413] text-white hover:opacity-90 disabled:opacity-60 transition"
            >
              {loading ? 'Ajout...' : 'Ajouter'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddReservationPopup;
